import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Close,
  Download,
  OpenInNew,
  Search,
  TableChart,
} from "@mui/icons-material";
import React, { useMemo, useState } from "react";
import useApiEp from "../../hooks/useApiEp";
import MuPb from "../../widgets/MuPb";
import { getErrorResponse } from "../../utils/util";

// ── helpers ──────────────────────────────────────────────────────────────────

const HIDDEN_DETAIL_KEYS = new Set(["_id", "__v"]);
const MAX_CELL_LEN = 40;

function deriveColumns(rows) {
  if (!rows?.length) return [];
  const freq = {};
  rows.forEach((r) =>
    Object.keys(r).forEach((k) => (freq[k] = (freq[k] || 0) + 1))
  );
  return Object.keys(freq).sort((a, b) => freq[b] - freq[a]);
}

function formatCell(val) {
  if (val == null) return "—";
  if (typeof val === "boolean") return val ? "Yes" : "No";
  if (typeof val === "object") return JSON.stringify(val);
  const str = String(val);
  return str.length > MAX_CELL_LEN ? str.slice(0, MAX_CELL_LEN) + "…" : str;
}

function formatLabel(key) {
  return key
    .replace(/([A-Z])/g, " $1")
    .replace(/[_-]/g, " ")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

function exportCSV(rows, columns, filename = "voice-reports.csv") {
  const header = columns.map(formatLabel).join(",");
  const body = rows
    .map((r) =>
      columns
        .map((c) => {
          const v = r[c] ?? "";
          const s = typeof v === "object" ? JSON.stringify(v) : String(v);
          return `"${s.replace(/"/g, '""')}"`;
        })
        .join(",")
    )
    .join("\n");
  const blob = new Blob([header + "\n" + body], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  Object.assign(document.createElement("a"), {
    href: url,
    download: filename,
  }).click();
  URL.revokeObjectURL(url);
}

function exportJSON(rows, filename = "voice-reports.json") {
  const blob = new Blob([JSON.stringify(rows, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  Object.assign(document.createElement("a"), {
    href: url,
    download: filename,
  }).click();
  URL.revokeObjectURL(url);
}

// ── Detail Modal ─────────────────────────────────────────────────────────────

function DetailModal({ row, onClose }) {
  if (!row) return null;
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <OpenInNew fontSize="small" color="primary" />
          <Typography variant="h6" fontWeight={600}>
            Report Details
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {Object.entries(row)
            .filter(([k]) => !HIDDEN_DETAIL_KEYS.has(k))
            .map(([key, val]) => (
              <Box key={key}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={600}
                  sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
                >
                  {formatLabel(key)}
                </Typography>
                <Box
                  sx={{
                    mt: 0.25,
                    p: 1,
                    borderRadius: 1,
                    bgcolor: "grey.50",
                    border: "1px solid",
                    borderColor: "grey.200",
                    wordBreak: "break-word",
                    fontSize: 14,
                  }}
                >
                  {typeof val === "object" ? (
                    <pre
                      style={{
                        margin: 0,
                        fontSize: 12,
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {JSON.stringify(val, null, 2)}
                    </pre>
                  ) : val == null ? (
                    <Typography variant="body2" color="text.disabled">
                      —
                    </Typography>
                  ) : typeof val === "boolean" ? (
                    <Chip
                      size="small"
                      label={val ? "Yes" : "No"}
                      color={val ? "success" : "default"}
                    />
                  ) : (
                    <Typography variant="body2">{String(val)}</Typography>
                  )}
                </Box>
              </Box>
            ))}
        </Box>
      </DialogContent>
    </Dialog>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

const VoiceFallback = () => {
  const { data, isLoading, isError, error } = useApiEp(
    `/voice/get-voice-reports`
  );

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState("");
  const [order, setOrder] = useState("asc");
  const [selectedRow, setSelectedRow] = useState(null);
  const [exportFmt, setExportFmt] = useState("csv");

  const columns = useMemo(() => deriveColumns(data), [data]);

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = search.toLowerCase();
    return data.filter((row) =>
      columns.some((c) =>
        String(row[c] ?? "")
          .toLowerCase()
          .includes(q)
      )
    );
  }, [data, search, columns]);

  const sorted = useMemo(() => {
    if (!orderBy) return filtered;
    return [...filtered].sort((a, b) => {
      const av = a[orderBy] ?? "";
      const bv = b[orderBy] ?? "";
      const cmp = String(av).localeCompare(String(bv), undefined, {
        numeric: true,
      });
      return order === "asc" ? cmp : -cmp;
    });
  }, [filtered, orderBy, order]);

  const paginated = sorted.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleSort = (col) => {
    if (orderBy === col) setOrder((o) => (o === "asc" ? "desc" : "asc"));
    else {
      setOrderBy(col);
      setOrder("asc");
    }
  };

  const handleExport = () => {
    if (exportFmt === "csv") exportCSV(filtered, columns);
    else exportJSON(filtered);
  };

  if (isLoading) return <MuPb />;
  if (isError) return <Alert severity="error">{getErrorResponse(error)}</Alert>;
  if (!data?.length)
    return (
      <Alert severity="info" icon={<TableChart />}>
        No voice reports found.
      </Alert>
    );

  return (
    <Box>
      {/* Toolbar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <TableChart color="primary" />
          <Typography variant="h6" fontWeight={600}>
            Voice Reports
          </Typography>
          <Chip
            label={`${filtered.length} records`}
            size="small"
            color="primary"
            variant="outlined"
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <TextField
            size="small"
            placeholder="Search all columns…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ width: 220 }}
          />

          <Select
            size="small"
            value={exportFmt}
            onChange={(e) => setExportFmt(e.target.value)}
            sx={{ minWidth: 80 }}
          >
            <MenuItem value="csv">CSV</MenuItem>
            <MenuItem value="json">JSON</MenuItem>
          </Select>

          <Tooltip
            title={`Export ${
              filtered.length
            } rows as ${exportFmt.toUpperCase()}`}
          >
            <Button
              variant="contained"
              size="small"
              startIcon={<Download />}
              onClick={handleExport}
            >
              Export
            </Button>
          </Tooltip>
        </Box>
      </Box>

      {/* Table */}
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
        <TableContainer sx={{ maxHeight: 520 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {columns.map((col) => (
                  <TableCell
                    key={col}
                    sx={{
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                      bgcolor: "grey.50",
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === col}
                      direction={orderBy === col ? order : "asc"}
                      onClick={() => handleSort(col)}
                    >
                      {formatLabel(col)}
                    </TableSortLabel>
                  </TableCell>
                ))}
                <TableCell
                  sx={{ fontWeight: 700, bgcolor: "grey.50", width: 110 }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {paginated.map((row, i) => (
                <TableRow
                  key={row._id ?? i}
                  hover
                  sx={{ "&:last-child td": { border: 0 } }}
                >
                  {columns.map((col) => (
                    <TableCell key={col} sx={{ maxWidth: 200 }}>
                      <Tooltip
                        title={String(row[col] ?? "")}
                        placement="top"
                        enterDelay={600}
                      >
                        <span
                          style={{
                            display: "block",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {formatCell(row[col])}
                        </span>
                      </Tooltip>
                    </TableCell>
                  ))}
                  <TableCell>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<OpenInNew fontSize="inherit" />}
                      onClick={() => setSelectedRow(row)}
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(+e.target.value);
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      {/* Detail Modal */}
      <DetailModal row={selectedRow} onClose={() => setSelectedRow(null)} />
    </Box>
  );
};

export default VoiceFallback;
